import { useEffect, useState } from 'react';
import { useWebSocket } from '@/features/conversations/hooks/useWebSocket';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type { 
  PlatformConnectedEvent, 
  PlatformDisconnectedEvent,
  FacebookAccount,
  InstagramAccount,
  WhatsAppAccount 
} from '@/types/api';

// Hook for real-time platform updates
export const usePlatformWebSocket = () => {
  const { subscribe } = useWebSocket();
  const queryClient = useQueryClient();
  const [recentUpdates, setRecentUpdates] = useState<Array<{
    id: string;
    type: string;
    platform: string;
    message: string;
    timestamp: Date;
  }>>([]);

  useEffect(() => {
    // Facebook platform events
    const unsubscribeFacebookConnected = subscribe('facebook_connected', (data: PlatformConnectedEvent) => {
      // Refresh connected platforms query
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });
      
      // Add to recent updates
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: 'connected',
        platform: 'Facebook',
        message: `${data.totalCount} Facebook page${data.totalCount !== 1 ? 's' : ''} connected`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]); // Keep only 10 recent updates
    });

    // Instagram platform events
    const unsubscribeInstagramConnected = subscribe('instagram_connected', (data: PlatformConnectedEvent) => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });
      
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: 'connected',
        platform: 'Instagram',
        message: `${data.totalCount} Instagram account${data.totalCount !== 1 ? 's' : ''} connected`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);
    });

    // WhatsApp platform events
    const unsubscribeWhatsAppConnected = subscribe('whatsapp_connected', (data: { account?: WhatsAppAccount }) => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });

      const name = data?.account?.businessName ?? 'your account';
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: 'connected',
        platform: 'WhatsApp',
        message: `WhatsApp Business "${name}" connected`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);
    });

    // Platform disconnection events
    const unsubscribePlatformDisconnected = subscribe('platform_disconnected', (data: PlatformDisconnectedEvent) => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });
      
      const platformName = data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: 'disconnected',
        platform: platformName,
        message: `${platformName} account disconnected`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);
    });

    // Platform connection errors
    const unsubscribePlatformError = subscribe('platform_error', (data: { platform: string; error: string; accountId?: string }) => {
      const platformName = data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
      toast.error(`${platformName} connection error: ${data.error}`);
      
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: 'error',
        platform: platformName,
        message: `Connection error: ${data.error}`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);
    });

    // Platform test results
    const unsubscribePlatformTestResult = subscribe('platform_test_result', (data: { 
      platform: string; 
      accountId: string; 
      success: boolean; 
      error?: string;
    }) => {
      const platformName = data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
      
      if (data.success) {
        toast.success(`${platformName} connection test successful!`);
      } else {
        toast.error(`${platformName} connection test failed: ${data.error}`);
      }
      
      setRecentUpdates(prev => [{
        id: Date.now().toString(),
        type: data.success ? 'test_success' : 'test_failed',
        platform: platformName,
        message: data.success ? 'Connection test successful' : `Connection test failed: ${data.error}`,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);
    });

    // Platform analytics updates
    const unsubscribePlatformAnalytics = subscribe('platform_analytics_update', (data: {
      platform: string;
      metrics: {
        messagesLastHour: number;
        activeConversations: number;
        responseRate: number;
      };
    }) => {
      // You could update a separate analytics state here
      console.log('Platform analytics update:', data);
    });

    return () => {
      unsubscribeFacebookConnected();
      unsubscribeInstagramConnected();
      unsubscribeWhatsAppConnected();
      unsubscribePlatformDisconnected();
      unsubscribePlatformError();
      unsubscribePlatformTestResult();
      unsubscribePlatformAnalytics();
    };
  }, [subscribe, queryClient]);

  return {
    recentUpdates,
    clearUpdates: () => setRecentUpdates([])
  };
};

// Hook specifically for platform connection status updates
export const usePlatformConnectionStatus = () => {
  const { subscribe } = useWebSocket();
  const [connectionStatus, setConnectionStatus] = useState<Record<string, {
    isConnected: boolean;
    lastUpdate: Date;
    error?: string;
  }>>({});

  useEffect(() => {
    const unsubscribeStatusUpdate = subscribe('platform_status_update', (data: {
      platform: string;
      accountId: string;
      isConnected: boolean;
      error?: string;
    }) => {
      setConnectionStatus(prev => ({
        ...prev,
        [`${data.platform}-${data.accountId}`]: {
          isConnected: data.isConnected,
          lastUpdate: new Date(),
          error: data.error
        }
      }));
    });

    return () => {
      unsubscribeStatusUpdate();
    };
  }, [subscribe]);

  return {
    connectionStatus,
    getStatus: (platform: string, accountId: string) => 
      connectionStatus[`${platform}-${accountId}`] || { isConnected: false, lastUpdate: new Date() }
  };
};

// Hook for real-time platform metrics
export const usePlatformMetrics = () => {
  const { subscribe } = useWebSocket();
  const [metrics, setMetrics] = useState<{
    facebook: { messages: number; conversations: number; responseRate: number };
    instagram: { messages: number; conversations: number; responseRate: number };
    whatsapp: { messages: number; conversations: number; responseRate: number };
    total: { messages: number; conversations: number; responseRate: number };
  }>({
    facebook: { messages: 0, conversations: 0, responseRate: 0 },
    instagram: { messages: 0, conversations: 0, responseRate: 0 },
    whatsapp: { messages: 0, conversations: 0, responseRate: 0 },
    total: { messages: 0, conversations: 0, responseRate: 0 }
  });

  useEffect(() => {
    const unsubscribeMetricsUpdate = subscribe('platform_metrics_update', (data) => {
      setMetrics(data);
    });

    return () => {
      unsubscribeMetricsUpdate();
    };
  }, [subscribe]);

  return metrics;
};